/**
 * Kimap 文件加密/解密工具
 * 简单的XOR加密方案
 */

// 加密密钥（可以自定义）
const ENCRYPTION_KEY = 'KIMAP_3D_MODEL_2024';

/**
 * 字符串转字节数组
 */
function stringToBytes(str: string): Uint8Array {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

/**
 * 字节数组转字符串
 */
function bytesToString(bytes: Uint8Array): string {
  const decoder = new TextDecoder();
  return decoder.decode(bytes);
}

/**
 * XOR加密/解密（对称加密）
 */
function xorCipher(data: Uint8Array, key: string): Uint8Array {
  const keyBytes = stringToBytes(key);
  const result = new Uint8Array(data.length);
  
  for (let i = 0; i < data.length; i++) {
    result[i] = data[i] ^ keyBytes[i % keyBytes.length];
  }
  
  return result;
}

/**
 * Base64编码
 */
function base64Encode(data: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]);
  }
  return btoa(binary);
}

/**
 * Base64解码
 */
function base64Decode(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * 加密OBJ内容为Kimap格式
 * @param objContent OBJ文件内容（字符串）
 * @returns 加密后的内容
 */
export function encryptToKimap(objContent: string): string {
  // 1. 转换为字节
  const objBytes = stringToBytes(objContent);
  
  // 2. XOR加密
  const encrypted = xorCipher(objBytes, ENCRYPTION_KEY);
  
  // 3. Base64编码（便于文本传输）
  const base64 = base64Encode(encrypted);
  
  // 4. 添加文件头标识
  const kimapContent = `KIMAP_V1\n${base64}`;
  
  return kimapContent;
}

/**
 * 解密Kimap内容为OBJ格式
 * @param kimapContent Kimap文件内容
 * @returns 解密后的OBJ内容
 */
export function decryptFromKimap(kimapContent: string): string {
  try {
    // 1. 检查文件头
    if (!kimapContent.startsWith('KIMAP_V1\n')) {
      throw new Error('无效的Kimap文件格式');
    }
    
    // 2. 移除文件头，获取Base64内容
    const base64Content = kimapContent.substring('KIMAP_V1\n'.length);
    
    // 3. Base64解码
    const encrypted = base64Decode(base64Content);
    
    // 4. XOR解密
    const decrypted = xorCipher(encrypted, ENCRYPTION_KEY);
    
    // 5. 转换为字符串
    const objContent = bytesToString(decrypted);
    
    return objContent;
  } catch (error) {
    throw new Error(`解密失败: ${error instanceof Error ? error.message : '未知错误'}`);
  }
}

/**
 * 验证是否为Kimap文件
 */
export function isKimapFile(content: string): boolean {
  return content.startsWith('KIMAP_V1\n');
}

/**
 * 从文件名判断是否为Kimap文件
 */
export function isKimapFilename(filename: string): boolean {
  return filename.toLowerCase().endsWith('.kimap');
}
